import { QueuedVideo } from '@/utils/optimistic/optimisticGenerate';
import { type UserImage } from '@/api/imageApi';
import { UploadingVideo } from '@/utils/optimistic/optimisticUpload';
import { truncateToWords, copyToClipboard } from '@/utils/common/textUtils';
import {
  ImageIcon,
  WarningIcon,
  CloseIcon,
  StateDisplay
} from '@/components/ui/common/GallerySharedComponents';
import { ImageTile, QueuedImageTile } from '@/components/ui/common';
import { CopyButton } from '@/components/ui/common/CopyButton';
import UploadingImagesSection from './UploadingImagesSection';

// Helper function for rendering queued image status content
export const renderQueuedImageStatus = (queuedImage: QueuedVideo, copied: boolean, setCopied: (value: boolean) => void) => {
  const handleCopyPrompt = () => {
    copyToClipboard(queuedImage.prompt);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (queuedImage.status === 'queued') {
    return (
      <>
        <div className="mb-3">
          <ImageIcon className="text-blue-400 mx-auto" />
        </div>
        <div className="text-lg font-semibold text-gray-600 mb-2">
          Queued
        </div>
        <div className="text-xs text-gray-600 mb-2 flex items-center justify-center gap-1">
          <span className="line-clamp-2">{truncateToWords(queuedImage.prompt).text}</span>
          <CopyButton copied={copied} onClick={handleCopyPrompt} colorClass="text-gray-600 hover:text-blue-500" />
        </div>
      </>
    );
  }

  if (queuedImage.status === 'processing') {
    return (
      <>
        <div className="mb-3">
          <ImageIcon className="text-blue-400 mx-auto" />
        </div>
        <div className="text-lg font-semibold text-gray-600 mb-2">
          Processing
        </div>
        <div className="w-24 h-2 bg-gray-300 rounded-full overflow-hidden mx-auto mb-2">
          <div
            className="h-full bg-blue-500 transition-all duration-300 ease-out rounded-full"
            style={{
              width: `${queuedImage.progress || 0}%`,
              background: 'linear-gradient(90deg, #3B82F6, #1D4ED8)'
            }}
          ></div>
        </div>
        <div className="text-xs text-gray-500 mb-2">
          {queuedImage.progress ? `${queuedImage.progress}%` : '0%'}
        </div>
        <div className="text-xs text-gray-600 flex items-center justify-center gap-1">
          <span className="line-clamp-2">{truncateToWords(queuedImage.prompt).text}</span>
          <CopyButton copied={copied} onClick={handleCopyPrompt} colorClass="text-gray-600 hover:text-blue-500" />
        </div>
      </>
    );
  }

  if (queuedImage.status === 'failed') {
    return (
      <>
        <div className="text-sm text-gray-700 font-medium mb-1">
          Generation Failed
        </div>
        <div className="text-xs text-gray-600 line-clamp-2">
          {queuedImage.prompt}
        </div>
      </>
    );
  }

  return null;
};

// Helper function for rendering queued image cancel button
export const renderQueuedImageCancelButton = (
  queuedImage: QueuedVideo,
  handleCancel?: () => void,
  cancelQueuedVideo?: (id: string) => void
) => {
  if (queuedImage.status === 'queued') {
    return (
      <button
        onClick={() => {
          handleCancel?.();
          cancelQueuedVideo?.(queuedImage.id);
        }}
        className="absolute top-1 right-1 bg-red-500 hover:bg-red-600 text-white rounded-full transition-colors shadow-lg z-20 h-6 flex items-center justify-center border border-red-400"
        title="Cancel"
      >
        <CloseIcon />
      </button>
    );
  }

  if (queuedImage.status === 'failed' && cancelQueuedVideo) {
    return (
      <button
        onClick={() => cancelQueuedVideo(queuedImage.id)}
        className="absolute top-1 right-1 bg-red-500 p-0 hover:bg-red-600 text-white rounded-full transition-colors shadow-lg z-20 h-6 w-6 flex items-center justify-center border border-red-400"
        title="Remove failed generation"
      >
        <CloseIcon />
      </button>
    );
  }

  return null;
};

// Helper function for images loading state
export const renderImagesLoadingState = () => (
  <StateDisplay
    icon={<div className="animate-spin w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full"></div>}
    iconBgColor="bg-blue-100"
    title="Loading Images..."
    description="Please wait while we fetch your images."
  />
);

// Helper function for images error state
export const renderImagesErrorState = (imagesError: string | null, onRetryImages?: () => void) => (
  <StateDisplay
    icon={<WarningIcon className="text-red-600" />}
    iconBgColor="bg-red-100"
    title="Error Loading Images"
    description={imagesError || "An error occurred while loading images."}
    actionButton={
      onRetryImages && (
        <button
          onClick={onRetryImages}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          Try Again
        </button>
      )
    }
  />
);

// Helper function for images empty state
export const renderImagesEmptyState = () => (
  <StateDisplay
    icon={<ImageIcon className="text-gray-400" />}
    iconBgColor="bg-gray-100"
    title="No Images Yet"
    description="Start generating images to see them here!"
  />
);

// Interface for images section parameters  
export interface ImagesSectionProps {
  filteredImages: UserImage[];
  queuedImages: QueuedVideo[];
  uploadingImages?: UploadingVideo[];
  isLoadingImages: boolean;
  imagesError: string | null;
  onRetryImages?: () => void;
  handleCancel?: () => void;
  cancelQueuedVideo?: (id: string) => void;
  handleImageClick?: (image: UserImage) => void;
}

// Main images section renderer
export const renderImagesSection = ({
  filteredImages,
  queuedImages,
  uploadingImages = [],
  isLoadingImages,
  imagesError,
  onRetryImages,
  handleCancel,
  cancelQueuedVideo,
  handleImageClick
}: ImagesSectionProps) => {
  const imageItems = filteredImages || [];
  const hasUploadingImages = uploadingImages.length > 0;
  const hasQueuedImages = queuedImages.length > 0;
  const hasRegularImages = imageItems.length > 0;
  const hasAnyImages = hasUploadingImages || hasQueuedImages || hasRegularImages;

  // Show loading state for images
  if (isLoadingImages && !hasAnyImages) {
    return renderImagesLoadingState();
  }

  // Show error state for images
  if (imagesError && !isLoadingImages) {
    return renderImagesErrorState(imagesError, onRetryImages);
  }

  // Show content when we have images or queued images
  if (hasAnyImages) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {/* Uploading Images */}
        <UploadingImagesSection uploadingImages={uploadingImages} />

        {/* Queued Images */}
        {queuedImages.map((queuedImage) => (
          <QueuedImageTile
            key={queuedImage.id}
            queuedImage={queuedImage}
            handleCancel={handleCancel}
            cancelQueuedVideo={cancelQueuedVideo}
          />
        ))}

        {/* Generated Images */}
        {imageItems.map((image) => (
          <ImageTile
            key={`${image.id}-${image.status}-${image.updated_at}`}
            image={image}
            onClick={() => handleImageClick?.(image)}
          />
        ))}
      </div>
    );
  }

  // Show empty state - no images at all
  return renderImagesEmptyState();
};